import React, { useEffect, useState } from 'react';
import Card, { CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import {
  MagnifyingGlassIcon,
  PlusIcon,
  PaperAirplaneIcon,
  XMarkIcon,
  ArrowPathIcon,
  CalendarIcon,
  TicketIcon,
} from '@heroicons/react/24/outline';
import tierInvitationService, { TierInvitation, TierInvitationStats } from '../../services/tierInvitationService';

const AdminTierInvitationsPage: React.FC = () => {
  const [invitations, setInvitations] = useState<TierInvitation[]>([]);
  const [stats, setStats] = useState<TierInvitationStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [selectedInvitation, setSelectedInvitation] = useState<TierInvitation | null>(null);

  const [createForm, setCreateForm] = useState({
    tierId: '',
    maxUses: '',
    expiresAt: '',
  });

  const [sendForm, setSendForm] = useState({
    recipientEmail: '',
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [invitationsData, statsData] = await Promise.all([
        tierInvitationService.getInvitations(),
        tierInvitationService.getStats(),
      ]);
      setInvitations(
        invitationsData.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      );
      setStats(statsData);
    } catch (error) {
      console.error('Failed to load tier invitations:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await tierInvitationService.createInvitation({
        tierId: createForm.tierId,
        maxUses: createForm.maxUses ? parseInt(createForm.maxUses) : undefined,
        expiresAt: createForm.expiresAt ? new Date(createForm.expiresAt) : undefined,
      });
      alert('Invitation created successfully!');
      setShowCreateModal(false);
      setCreateForm({ tierId: '', maxUses: '', expiresAt: '' });
      loadData();
    } catch (error) {
      console.error('Failed to create invitation:', error);
      alert('Failed to create invitation. Please try again.');
    }
  };

  const handleSendInvitation = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedInvitation) return;
    try {
      await tierInvitationService.sendInvitation(selectedInvitation.id, sendForm.recipientEmail);
      alert(`Invitation sent to ${sendForm.recipientEmail}!`);
      setShowSendModal(false);
      setSendForm({ recipientEmail: '' });
      setSelectedInvitation(null);
    } catch (error) {
      console.error('Failed to send invitation:', error);
      alert('Failed to send invitation.');
    }
  };

  const handleDeactivate = async (invitation: TierInvitation) => {
    if (!confirm('Are you sure you want to deactivate this invitation?')) return;
    try {
      await tierInvitationService.deactivateInvitation(invitation.id);
      loadData();
    } catch (error) {
      console.error('Failed to deactivate invitation:', error);
      alert('Failed to deactivate invitation.');
    }
  };

  const handleDelete = async (invitation: TierInvitation) => {
    if (!confirm('Are you sure you want to delete this invitation? This action cannot be undone.')) return;
    try {
      await tierInvitationService.deleteInvitation(invitation.id);
      loadData();
    } catch (error) {
      console.error('Failed to delete invitation:', error);
      alert('Failed to delete invitation.');
    }
  };

  const openSendModal = (invitation: TierInvitation) => {
    setSelectedInvitation(invitation);
    setShowSendModal(true);
  };

  const copyInvitationCode = (code: string) => {
    navigator.clipboard.writeText(code);
    alert('Invitation code copied to clipboard!');
  };

  const filteredInvitations = invitations.filter((inv) => {
    const search = searchTerm.toLowerCase();
    return (
      inv.invitationCode?.toLowerCase().includes(search) ||
      inv.tier?.name?.toLowerCase().includes(search) ||
      inv.creator?.displayName?.toLowerCase().includes(search)
    );
  });

  const getStatusBadge = (invitation: TierInvitation) => {
    if (!invitation.isActive) {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-200 text-gray-700">Inactive</span>;
    }
    if (invitation.expiresAt && new Date(invitation.expiresAt) < new Date()) {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-200 text-red-700">Expired</span>;
    }
    if (invitation.maxUses && invitation.currentUses >= invitation.maxUses) {
      return <span className="px-2 py-1 text-xs rounded-full bg-yellow-200 text-yellow-700">Fully Used</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-green-200 text-green-700">Active</span>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <ArrowPathIcon className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading tier invitations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Tier Invitations Manager</h1>
          <p className="text-gray-600 mt-1">Manage invitation codes for exclusive tier access</p>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="flex items-center gap-2">
          <PlusIcon className="w-5 h-5" />
          Create Invitation
        </Button>
      </div>

      {/* Statistics Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Invitations</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <TicketIcon className="w-8 h-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Active</p>
                  <p className="text-2xl font-bold text-green-600">{stats.active}</p>
                </div>
                <TicketIcon className="w-8 h-8 text-green-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Expired</p>
                  <p className="text-2xl font-bold text-red-600">{stats.expired}</p>
                </div>
                <CalendarIcon className="w-8 h-8 text-red-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Fully Used</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.fullyUsed}</p>
                </div>
                <TicketIcon className="w-8 h-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Total Uses</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.totalUses}</p>
                </div>
                <TicketIcon className="w-8 h-8 text-purple-600" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Card>
        <CardContent className="p-4">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by code, tier, or creator..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </CardContent>
      </Card>

      {/* Invitations Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Code
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Tier
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Creator
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Uses
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Expires
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInvitations.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                      No invitations found
                    </td>
                  </tr>
                ) : (
                  filteredInvitations.map((invitation) => (
                    <tr key={invitation.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => copyInvitationCode(invitation.invitationCode)}
                          className="font-mono text-sm font-medium text-blue-600 hover:text-blue-800"
                        >
                          {invitation.invitationCode}
                        </button>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{invitation.tier?.name || 'N/A'}</div>
                        <div className="text-sm text-gray-500">
                          {invitation.tier?.currency} {invitation.tier?.price}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{invitation.creator?.displayName || 'N/A'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {invitation.currentUses}
                          {invitation.maxUses ? ` / ${invitation.maxUses}` : ' / ∞'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-500">
                          {invitation.expiresAt
                            ? new Date(invitation.expiresAt).toLocaleDateString()
                            : 'Never'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(invitation)}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            onClick={() => openSendModal(invitation)}
                            size="sm"
                            variant="ghost"
                            title="Send invitation"
                          >
                            <PaperAirplaneIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDeactivate(invitation)}
                            size="sm"
                            variant="ghost"
                            title="Deactivate"
                            disabled={!invitation.isActive}
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </Button>
                          <Button
                            onClick={() => handleDelete(invitation)}
                            size="sm"
                            variant="ghost"
                            className="text-red-600 hover:text-red-800"
                            title="Delete"
                          >
                            <XMarkIcon className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Create Invitation Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create Tier Invitation</h2>
            <form onSubmit={handleCreateInvitation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tier ID *</label>
                <input
                  type="text"
                  value={createForm.tierId}
                  onChange={(e) => setCreateForm({ ...createForm, tierId: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  placeholder="Enter tier ID"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Max Uses (Optional)</label>
                <input
                  type="number"
                  value={createForm.maxUses}
                  onChange={(e) => setCreateForm({ ...createForm, maxUses: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Leave empty for unlimited"
                  min="1"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expires At (Optional)</label>
                <input
                  type="datetime-local"
                  value={createForm.expiresAt}
                  onChange={(e) => setCreateForm({ ...createForm, expiresAt: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1">
                  Create Invitation
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowCreateModal(false);
                    setCreateForm({ tierId: '', maxUses: '', expiresAt: '' });
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Send Invitation Modal */}
      {showSendModal && selectedInvitation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Send Invitation</h2>
            <p className="text-sm text-gray-600 mb-4">
              Send invitation code <span className="font-mono font-medium">{selectedInvitation.invitationCode}</span> to:
            </p>
            <form onSubmit={handleSendInvitation} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Recipient Email *</label>
                <input
                  type="email"
                  value={sendForm.recipientEmail}
                  onChange={(e) => setSendForm({ ...sendForm, recipientEmail: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                  placeholder="patron@example.com"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="submit" className="flex-1 flex items-center justify-center gap-2">
                  <PaperAirplaneIcon className="w-4 h-4" />
                  Send Invitation
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowSendModal(false);
                    setSendForm({ recipientEmail: '' });
                    setSelectedInvitation(null);
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTierInvitationsPage;
